import { TipoLogradouroPaisFields } from 'app/modules/apps/shared/endereco/TipoLogradouroPaisFields'
import {
  dtoTipoLogradouroPais,
  valoresIniciaisTipoLogradouroPais,
} from 'app/modules/apps/shared/endereco/tipoLogradouroPais'
import { useFormik } from 'formik'
import { FC, useEffect, useMemo, useState } from 'react'

import {
  ButtonType,
  MaskType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  FormInput,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { getEstadoByUf } from '../../../../atendimento_medicos/atendimentos-medico-list/core/_requests'
import {
  EnderecoDtoCreate,
  EnderecoDtoUpdate,
  EnderecoList,
} from '../../../../empresa/empresas-list/core/_models'
import { createEnderecoDaOrigem, OrigemEndereco, updateEnderecoDaOrigem } from '../../core/_origem'
import { findTipoEnderecoPorDescricao, useOptionsTiposEndereco } from '../../core/_tiposEndereco'
import { enderecoSchema } from './validation/enderecoSchema'
import { useT } from '_metronic/i18n/Metronici18n'

type IProps = {
  origem: OrigemEndereco
  idOrigem: number
  /** Ausente ao cadastrar; preenchido ao alterar um endereço da listagem. */
  endereco?: EnderecoList
}

export const EnderecoModal: FC<IProps> = ({ origem, idOrigem, endereco }) => {
  const { closeModal } = useModal()
  const { t } = useT()
  const optionsTiposEndereco = useOptionsTiposEndereco()

  const isEdicao = !!endereco
  const [ufOption, setUfOption] = useState<IOption<string> | undefined>(undefined)

  // O endpoint de empresa serializa a UF do endereço como `UF` (maiúsculo, do getter `getUF()` da
  // projeção do backend); os demais usam `uf`. Lemos os dois para não depender da grafia.
  const ufSigla = endereco?.uf ?? endereco?.UF

  const initialValues = useMemo(
    () => ({
      tipo: findTipoEnderecoPorDescricao(optionsTiposEndereco, endereco?.tipoEndereco),
      nome: endereco?.nome || '',
      cep: endereco?.cep || '',
      logradouro: endereco?.logradouro || '',
      numero: endereco?.numero || '',
      complemento: endereco?.complemento || '',
      bairro: endereco?.bairro || '',
      cidade: endereco?.cidade || '',
      uf: ufOption,
      ...valoresIniciaisTipoLogradouroPais(endereco),
    }),
    [endereco, optionsTiposEndereco, ufOption]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => enderecoSchema(isEdicao), [isEdicao]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (endereco) {
          const dados: EnderecoDtoUpdate = {
            id: endereco.id,
            cep: values.cep,
            logradouro: values.logradouro,
            numero: values.numero,
            complemento: values.complemento || null,
            bairro: values.bairro,
            cidade: values.cidade,
            uf: values.uf?.value as string,
            ...dtoTipoLogradouroPais(values),
            ativo: endereco.ativo ?? true,
          }
          await updateEnderecoDaOrigem(origem, idOrigem, dados)
        } else {
          const dados: EnderecoDtoCreate = {
            tipo: Number(values.tipo?.value),
            nome: values.nome || undefined,
            cep: values.cep,
            logradouro: values.logradouro,
            numero: values.numero,
            complemento: values.complemento || null,
            bairro: values.bairro,
            cidade: values.cidade,
            uf: values.uf?.value as string,
            ...dtoTipoLogradouroPais(values),
          }
          await createEnderecoDaOrigem(origem, idOrigem, dados)
        }

        await showAlert({
          title: 'Sucesso!',
          text: `Endereço ${endereco ? 'alterado' : 'cadastrado'} com sucesso!`,
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    let ativo = true

    const carregarUf = async () => {
      if (!ufSigla) return
      try {
        const estado = await getEstadoByUf(ufSigla)
        // Descarta resposta obsoleta ou setState após o modal fechar (unmount).
        if (!ativo) return
        setUfOption({ label: estado.descricao, value: estado.sigla })
      } catch (error) {
        showError(error)
      }
    }

    carregarUf()

    return () => {
      ativo = false
    }
  }, [ufSigla])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='tipo'
            label='Tipo de Endereço'
            placeholder='Selecione o tipo de endereço'
            options={optionsTiposEndereco}
            isClearable={false}
            isDisabled={isEdicao || formik.isSubmitting}
            infoText={
              isEdicao
                ? 'O tipo não pode ser alterado depois que o endereço é cadastrado.'
                : 'Comercial e Prescrição permitem apenas um endereço por cadastro.'
            }
            isRequired={!isEdicao}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='nome'
            label='Nome do Endereço'
            placeholder='Digite um nome para identificar o endereço'
            isDisabled={isEdicao}
            infoText={
              isEdicao
                ? 'O nome não pode ser alterado depois que o endereço é cadastrado.'
                : 'Opcional. Ajuda a identificar o endereço na listagem.'
            }
          />
        </div>
      </div>

      <TipoLogradouroPaisFields formik={formik} prefixo='' novo={!endereco} idPrefix='endereco' />
      <div className='row mb-3'>
        <div className='col-md-8'>
          <FormInput
            formik={formik}
            name='logradouro'
            label='Logradouro'
            placeholder='Logradouro'
            isRequired
          />
        </div>
        <div className='col-md-4'>
          <FormInput formik={formik} name='numero' label='Número' placeholder='Número' isRequired />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-8'>
          <FormInput
            formik={formik}
            name='complemento'
            label='Complemento'
            placeholder='Complemento'
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='cep'
            label='CEP'
            placeholder='00.000-000'
            mask={MaskType.CEP}
            isRequired
          />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-8'>
          <FormInput formik={formik} name='bairro' label='Bairro' placeholder='Bairro' isRequired />
        </div>
        <div className='col-md-4'>
          <FormInput formik={formik} name='cidade' label='Cidade' placeholder='Cidade' isRequired />
        </div>
      </div>

      <FormSelect
        className='mb-3'
        formik={formik}
        name='uf'
        label='Estado'
        placeholder='Selecione um estado'
        fetchOptions={OptionsHelper.getOptionsEstadosUf}
        isClearable={false}
        isDisabled={formik.isSubmitting}
        isRequired
      />

      <div className='d-flex justify-content-center mt-5'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
